import React from 'react'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { format } from '@e3dc-react/shell/Libs/date'
import { Box, CircularProgress, Stack, Tooltip, Typography, useTheme, useMediaQuery } from '@mui/material'
import { Chip } from 'src/Components'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import SyncIcon from '@mui/icons-material/Sync'
import { useConfigSync } from 'src/Libs/installations/hooks/installation-configuration/use-config-sync'
import { DeviceLinkModel } from 'src/Libs/installations'
import { t } from 'i18next'
import { usePlannedInstallationDevices } from 'src/Libs/installations/hooks/installation-configuration/use-planned-installation-devices'

interface Props {
  installationId: number
  /** The installation's EM 360° (emc master device). Sync is only possible once this is paired. */
  emcDeviceLink?: DeviceLinkModel
}

interface SyncStatusDisplayProps {
  isBusy: boolean
  showNotConnectedMessage: boolean
  hasError: boolean
  hasSyncedStatus: boolean
  lastSyncedAt?: string
  isOffline: boolean
}

const SyncStatusDisplay: React.FC<SyncStatusDisplayProps> = ({
  isBusy,
  showNotConnectedMessage,
  hasError,
  hasSyncedStatus,
  lastSyncedAt,
  isOffline,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (isBusy) {
    return <CircularProgress size={16} sx={{ mt: 1 }} />
  }

  if (showNotConnectedMessage) {
    return (
      <Typography variant={'body2'} color={'text.secondary'}>
        {t('installation-configuration.offline-sync.not-connected-message')}
      </Typography>
    )
  }

  let statusContent

  if (!isMobile) {
    if (isOffline) {
      statusContent = <Chip textKey={'installation-configuration.offline-sync.status.offline'} paletteColor={theme.palette.error} isLoading={false} />
    } else if (hasError) {
      statusContent = <Chip textKey={'installation-configuration.offline-sync.status.error'} paletteColor={theme.palette.error} isLoading={false} />
    } else if (hasSyncedStatus) {
      statusContent = (
        <Chip textKey={'installation-configuration.offline-sync.status.synced'} paletteColor={theme.palette.primary} isLoading={false} />
      )
    } else {
      statusContent = (
        <Typography variant={'body2'} color={'text.secondary'}>
          {t('installation-configuration.offline-sync.not_synced_message')}
        </Typography>
      )
    }
  }

  return (
    <Stack direction={'row'} alignItems={'center'} gap={2}>
      {statusContent}
      {lastSyncedAt && (
        <Typography variant={'body2'} color={'text.secondary'}>
          {t('installation-configuration.offline-sync.last-synced', {
            date: format(new Date(lastSyncedAt), t('common.format.date-fns-date-time')),
          })}
        </Typography>
      )}
    </Stack>
  )
}

const OfflineSyncPanel: React.FC<Props> = ({ installationId, emcDeviceLink }) => {
  const { t } = useCustomTranslation()
  const { isEmcOnline } = usePlannedInstallationDevices(installationId, emcDeviceLink?.deviceId ?? '')

  /** The backend can only run a sync once the EM 360° has a deviceId and is fully paired. */
  const isSyncPossible = !!emcDeviceLink?.deviceId && emcDeviceLink?.pairingState === 'paired'

  const { devices, isLoadingStatus, isSyncing, hasSyncError, isStatusError, startSync } = useConfigSync(installationId, isSyncPossible)

  const isBusy = isLoadingStatus || isSyncing
  const showNotConnectedMessage = !isBusy && !isSyncPossible
  const hasError = !isBusy && isSyncPossible && (isStatusError || hasSyncError || devices.some(device => device.state !== 'done'))
  const hasSyncedStatus = !hasError && devices.length > 0

  const lastSyncedAt = devices.reduce<string | undefined>(
    (latest, device) => (device.lastUpdate && (!latest || device.lastUpdate > latest) ? device.lastUpdate : latest),
    undefined
  )

  return (
    <AccordionItem title={t('installation-configuration.offline-sync.title')} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
        <Box>
          <SyncStatusDisplay
            isBusy={isBusy}
            showNotConnectedMessage={showNotConnectedMessage}
            hasError={hasError}
            hasSyncedStatus={hasSyncedStatus}
            lastSyncedAt={lastSyncedAt}
            isOffline={!isEmcOnline}
          />
        </Box>
        <Tooltip title={isBusy || isSyncPossible ? '' : t('installation-configuration.offline-sync.not-connected-tooltip')}>
          <span>
            <LoadingButton
              variant={'contained'}
              startIcon={<SyncIcon />}
              loading={isSyncing}
              loadingPosition={'start'}
              disabled={!isSyncPossible || isSyncing}
              onClick={startSync}
            >
              {t('installation-configuration.offline-sync.sync-button')}
            </LoadingButton>
          </span>
        </Tooltip>
      </Stack>
    </AccordionItem>
  )
}

export default OfflineSyncPanel
